import { useCallback } from 'react';
import { useClient } from 'urql';

import { useTelemetry } from '@order/Telemetry';

import {
  ScanToPayPaymentMethodsDocument,
  type ScanToPayPaymentMethodsQuery,
} from '../../graphql/ScanToPayPaymentMethods.generated';
import { type PaymentMethod } from '../../machine/scan-to-pay.machine.types';

// ────────────────────────────────────────────────────────────────────────────

/**
 * A hook for fetching the available payment methods.
 */
export const useScanToPayPaymentMethods = () => {
  const client = useClient();
  const { track } = useTelemetry();

  // ─── Queries ──────────────────────────────────────────────────────────────

  const fetchPaymentMethods = useCallback(async () => {
    track('scan-to-pay.stripe-usage', { event: 'fetch-payment-methods' });

    const queryPaymentMethods = client.query<ScanToPayPaymentMethodsQuery>;

    const response = await queryPaymentMethods(
      ScanToPayPaymentMethodsDocument,
      {},
      { requestPolicy: 'network-only' },
    ).toPromise();

    const paymentMethods = response.data?.paymentMethodCards ?? [];

    track('scan-to-pay.stripe-usage', {
      event: 'fetch-payment-methods-success',
      paymentMethods,
    });

    return { paymentMethods: paymentMethods as readonly PaymentMethod[] };
  }, [client.query, track]);

  // ──────────────────────────────────────────────────────────────────────────

  return { fetchPaymentMethods };
};
